import { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { saveEvent } from "../../../api/userevents";
import { useCourse } from "../../../contexts/CourseProvider";
import { getItem, setItem } from "../../../utils/persistanceStorage";
import { useNavigate } from "react-router";
import generalApi from "../../../api/general";
import SurveyText from "./SurveyText";
import SurveyComplete from "./SurveyComplete";
import SurveyBlock from "./SurveyBlock";
import xapiverbs from "../../../config/xapiverbs";
import { answerOnboardingQuestion } from "../../../api/postactivity";
import { shuffle } from "lodash";

const SurveyActivity = ({ activityid, onSubmit }) => {
  const params = useParams();
  const navigate = useNavigate();
  const { course } = useCourse();
  const [responses, setResponses] = useState({});
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const hasLoggedLoadRef = useRef(false);

  useEffect(() => {
    hasLoggedLoadRef.current = false;
  }, [activityid, course.courseid]);

  useEffect(() => {
    hasLoggedLoadRef.current = true;
    saveEvent(activityid, course.courseid, xapiverbs.LOAD, {
      component: "SurveyActivity",
      timestamp: Date.now(),
    });
  }, [activityid, course.courseid]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const res = await generalApi.getActivityDetails(activityid, course.courseid);
        const data = res?.data?.mainActivity?.datajson ?? res?.data?.mainActivity ?? res?.data;
        // const normalized = normalizeSurvey(raw);

        const surveyDataRandomized = {
          ...data,
          survey_blocks:
            data.survey_blocks?.map((block) => ({
              ...block,
              items: block.randomize ? shuffle(block.items) : block.items,
            })) ?? [],
        };
        setSurveyData(surveyDataRandomized);
      } finally {
        console.log(surveyData);
        if (!cancelled) setLoading(false);
      }
    }
    if (activityid) load();
    return () => {
      cancelled = true;
    };
  }, [activityid, course.courseid]);

  const allItems = useMemo(
    () => (surveyData?.survey_blocks ?? []).flatMap((block) => block.items.map((item) => item)),
    [surveyData]
  );
  const totalQuestions = allItems.length;
  const handleItemChange = (item, value) => {
    setResponses((prev) => ({
      ...prev,
      [item.itemid]: value,
    }));

    saveEvent(activityid, course.courseid, xapiverbs.ANSWERED, {
      component: "SurveyActivity",
      code: item.code,
      value,
    });
  };

  const isItemAnswered = (item) => {
    const value = responses[item.itemid];
    if (item.itemtype === "text") {
      return typeof value === "string" && value.trim().length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };
  const answeredCount = allItems.filter(isItemAnswered).length;
  const allAnswered = answeredCount === totalQuestions && totalQuestions > 0;

  const handleSubmit = async () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);

    const completedAt = new Date().toISOString();

    const answers = allItems.map((item) => ({ code: item.code, value: responses[item.itemid] }));

    await answerOnboardingQuestion(activityid, { answers, completedAt });

    await saveEvent(activityid, course.courseid, xapiverbs.SUBMITTED, {
      component: "SurveyActivity",
      answeredCount,
      totalQuestions,
      completedAt,
    });

    setSubmitting(false);
    setCompleted(true);
  };

  if (loading) {
    return (
      <div className="w-[90%] md:w-[80%] mx-auto bg-slate-50 shadow p-2 md:p-8">
        <div className="text-sm text-slate-600">Loading survey…</div>
      </div>
    );
  }

  if (!surveyData) return null;
  if (completed) return <SurveyComplete surveyData={surveyData} onClick={onSubmit} />;

  return (
    <div className="w-[80%] md:w-[70%] mx-auto bg-slate-50 shadow md:p-8 mt-1">
      <header className="mb-6 border-b pb-4">
        <h1 className="text-3xl font-semibold text-slate-900">{surveyData.survey_heading}</h1>
        {surveyData.survey_text && (
          <div className="mt-2 text-xl text-slate-600">
            <SurveyText text={surveyData.survey_text} />
          </div>
        )}
      </header>

      {surveyData.survey_blocks.map((block) => (
        <SurveyBlock key={block.blockid} block={block} responses={responses} onItemChange={handleItemChange} />
      ))}

      <div className="mt-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          {answeredCount}/{totalQuestions} questions answered
        </div>

        {!allAnswered && <div className="text-xs text-amber-600 md:text-right">Please answer all items to continue.</div>}
      </div>

      <div className="flex justify-center mt-3">
        {allAnswered ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            aria-busy={submitting}
            className="text-xl inline-flex items-center justify-center rounded-full bg-accent px-5 py-2 text-white shadow-sm hover:bg-accentdark disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        ) : (
          <button
            type="button"
            disabled={true}
            aria-busy={true}
            className="text-xl inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-white shadow-sm opacity-60 cursor-not-allowed"
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
};

export default SurveyActivity;
